import { calendarPickerAriaLabel } from './CalendarChannelPicker.locstring.json';
import CalendarChannelPickerProps from './CalendarChannelPicker.props';
import { getStore } from '../store/store';
import { Icon } from '@fluentui/react/lib/Icon';
import { Image, ImageFit } from '@fluentui/react/lib/Image';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import { observer } from 'mobx-react-lite';
import { Calendar } from 'owa-calendar-cache';
import { getCalendarColorScheme } from 'owa-calendar-color-resolver';
import { CompositePicker, PickerOption } from 'owa-calendar-composite-picker';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import * as React from 'react';
import formatSelectedOptionName, {
    formatDefaultSelectedCalendarName,
} from '../utils/formatSelectedOptionName';
import {
    isCalendarOption,
    isChannelOption,
    isTeamOption,
    TeamOption,
    CalendarOption,
    ChannelOption,
} from '../store/schema/CalendarChannelPickerInterfaces';

const styles = require('./CalendarChannelPicker.scss');
const classNames = require('classnames/bind').bind(styles);

export default observer(function CalendarChannelPicker(props: CalendarChannelPickerProps) {
    const formatOptionName = option => formatSelectedOptionName(props.event, option);
    const onChange = option => {
        if (isCalendarOption(option)) {
            props.onCalendarIdChanged(((option as CalendarOption).calendar as Calendar).id);
        } else if (isChannelOption(option)) {
            props.onChannelChanged(option as ChannelOption);
        }
    };

    const highlightedOptionButtonStyle = mergeStyles({
        backgroundColor: props.colorScheme.calLighter + '!important',
    });
    const defaultValue = formatDefaultSelectedCalendarName(props.event);

    return (
        <CompositePicker
            viewState={getStore()}
            {...props}
            renderOption={renderOption}
            defaultValue={defaultValue}
            formatOptionName={formatOptionName}
            onChange={onChange}
            styles={{
                ...props.styles,
                pickerContainer: styles.calendarPickerContainer,
                textField: classNames(props.styles?.textField, styles.textField),
                textFieldGroup: styles.textFieldGroup,
                textFieldSuffix: styles.textFieldSuffix,
                textFieldRoot: classNames(props.styles?.textFieldRoot, styles.textFieldRoot),
                optionsContainer: classNames(
                    props.styles?.optionsContainer,
                    styles.optionsContainer
                ),
                optionButton: styles.optionButton,
                highlightedOptionButton: highlightedOptionButtonStyle,
            }}
            pickerAriaLabel={loc(calendarPickerAriaLabel)}
            useOptionLengthAsTextFieldWidth={true}
            hideParentOptionIfEmptyLeafOptions={true}
            showChevronDownButton={true}
        />
    );
});

/**
 * Renders a Picker Option
 * @param option The PickerOption to render. It could be a Calendar, a Team or a Channel
 */
function renderOption(option: PickerOption) {
    if (isCalendarOption(option)) {
        return renderCalendarOption(option as CalendarOption);
    }

    if (isTeamOption(option)) {
        return renderTeamOption(option as TeamOption);
    }

    return renderChannelOption(option);
}

/**
 * Renders a Calendar Option
 * @param calendar The CalendarOption to render
 */
function renderCalendarOption(calendar: CalendarOption) {
    return (
        <>
            <Icon
                iconName={ControlIcons.CircleFill}
                className={styles.circleFillIcon}
                styles={{
                    root: {
                        color: getCalendarColorScheme(calendar.calendar.id).calPrimary,
                    },
                }}
            />
            <span className={styles.optionText}>{calendar.displayName}</span>
        </>
    );
}

/**
 * Renders a Team option
 * @param team The Team option to render.
 */
function renderTeamOption(team: TeamOption) {
    return (
        <>
            {team.profilePictureUrl && (
                <Image
                    className={styles.teamProfilePicture}
                    src={team.profilePictureUrl}
                    imageFit={ImageFit.cover}
                />
            )}
            <span className={styles.optionText}>{team.displayName}</span>
        </>
    );
}

/**
 * Renders a Channel option
 * @param option The Channel option to render.
 */
function renderChannelOption(option: PickerOption) {
    return <span className={styles.channelText}>{option.displayName}</span>;
}
